import React, { useState,useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  useTheme,
  Grid,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as GuestIcon,
  Business as PartnerIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Phone as PhoneIcon,
  PersonOutline as PersonOutlineIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import masterService from '../../services/masterService';

const RegisterPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { register: registerUser, gustRegister, isRegistering, isGustRegistering } = useAuth();

  const [userRegisterOpen, setUserRegisterOpen] = useState(false);
  const [guestRegisterOpen, setGuestRegisterOpen] = useState(false);
  const [guestActiveTab, setGuestActiveTab] = useState(0);
  const [userRegisterError, setUserRegisterError] = useState('');
  const [guestRegisterError, setGuestRegisterError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Location dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  
  // Selected values for maintaining state
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const userForm = useForm();
  const guestForm = useForm();

  const handleUserRegister = async (data) => {
    try {
      setUserRegisterError('');
      
      // Ensure we're sending IDs instead of names
      const formData = {
        ...data,
        country: selectedCountry?.id || data.country,
        state: selectedState?.id || data.state,
        city: selectedCity?.id || data.city
      };
      
      await registerUser(formData);
      setUserRegisterOpen(false);
    } catch (err) {
      setUserRegisterError(err.response?.data?.message || 'Registration failed');
    }
  };

  const handleGuestRegister = async (data) => {
    try {
      setGuestRegisterError('');
      await gustRegister(data);
      setGuestRegisterOpen(false);
    } catch (err) {
      setGuestRegisterError(err.response?.data?.message || 'Registration failed');
    }
  };

  const handlePartnerRegister = () => {
    window.open('https://seethakalyanavaibhogame.com/skvadmin/partner-register', '_blank');
  };

  const handleGuestTabChange = (event, newValue) => {
    setGuestActiveTab(newValue);
    setGuestRegisterError('');
    guestForm.reset();
  };

  const handleCloseUserRegister = () => {
    setUserRegisterOpen(false);
    setUserRegisterError('');
    userForm.reset();
    // Reset location selections
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setStates([]);
    setCities([]);
  };

  const handleCloseGuestRegister = () => {
    setGuestRegisterOpen(false);
    setGuestRegisterError('');
    setGuestActiveTab(0);
    guestForm.reset();
  };

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const countriesData = await masterService.getCountryList();
        setCountries(countriesData);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Handle country selection
  const handleCountryChange = async (countryId) => {
    const country = countries.find(c => c.id === countryId);
    setSelectedCountry(country || null);
    setSelectedState(null);
    setSelectedCity(null);
    
    userForm.setValue('country', countryId, { shouldValidate: true });
    userForm.setValue('state', '', { shouldValidate: true });
    userForm.setValue('city', '', { shouldValidate: true });
    setStates([]);
    setCities([]);
    
    if (countryId) {
      setLoadingStates(true);
      try {
        const statesData = await masterService.getStatesList(countryId);
        setStates(statesData);
      } catch (error) {
        console.error('Error fetching states:', error);
      } finally {
        setLoadingStates(false);
      }
    }
  };

  // Handle state selection
  const handleStateChange = async (stateId) => {
    const state = states.find(s => s.id === stateId);
    setSelectedState(state || null);
    setSelectedCity(null);
    
    userForm.setValue('state', stateId, { shouldValidate: true });
    userForm.setValue('city', '', { shouldValidate: true });
    setCities([]);
    
    if (stateId) {
      setLoadingCities(true);
      try {
        const citiesData = await masterService.getCitiesList(stateId);
        setCities(citiesData);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setLoadingCities(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        py: { xs: 4, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 0% 0%, rgba(92,15,47,0.05) 0%, transparent 50%)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '-10%',
          width: '40%',
          height: '40%',
          background: 'radial-gradient(circle, rgba(92,15,47,0.1) 0%, transparent 70%)',
          transform: 'translateY(-50%)',
          zIndex: 0
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 10,
          display: 'flex',
          gap: 2
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <HomeIcon />
        </IconButton>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                textAlign: 'center',
                mb: { xs: 4, md: 0 }
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: '#333',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 10px rgba(92,15,47,0.2)'
                }}
              >
                Join Seetha Kalyana Vaibhogame
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Register to start your journey to find your perfect match
              </Typography>
              {/* <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 4
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <PersonIcon />
                  <Typography>User Registration</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <GuestIcon />
                  <Typography>Guest Registration</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#5c0f2f',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                  }
                }}>
                  <PartnerIcon />
                  <Typography>Partner Registration</Typography>
                </Box>
              </Box> */}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 25px rgba(92,15,47,0.2)'
                }
              }}
            >
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 700,
                  color: '#333',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #5c0f2f, #7d1f4f)',
                    borderRadius: '2px'
                  }
                }}
              >
                Register
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                justifyContent: 'center',
                alignItems: 'stretch',
                flexWrap: 'wrap'
              }}>
                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={() => setUserRegisterOpen(true)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <PersonIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                      User Registration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create an account to access all features
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Register
                    </Button>
                  </CardContent>
                </Card>

                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={() => setGuestRegisterOpen(true)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <GuestIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                      Guest Registration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Register as a guest with limited access
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Register
                    </Button>
                  </CardContent>
                </Card>

                <Card
                  elevation={4}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 30%' },
                    maxWidth: { xs: '100%', md: '350px' },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(92,15,47,0.05) 0%, rgba(125,31,79,0.05) 100%)',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(92,15,47,0.2)',
                      border: '2px solid rgba(92,15,47,0.3)',
                      background: 'linear-gradient(135deg, rgba(92,15,47,0.1) 0%, rgba(125,31,79,0.1) 100%)',
                    },
                  }}
                  onClick={handlePartnerRegister}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                    }}>
                      <PartnerIcon
                        sx={{
                          fontSize: 40,
                          color: 'white',
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#5c0f2f' }}>
                      Partner Registration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Register as a partner for business opportunities
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                        },
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Register
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* User Register Dialog */}
      <Dialog
        open={userRegisterOpen}
        onClose={handleCloseUserRegister}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5c0f2f 0%, #7d1f4f 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3,
          fontSize: '1.5rem',
          fontWeight: 700
        }}>
          User Registration
          <IconButton onClick={handleCloseUserRegister} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {userRegisterError && (
            <Typography
              color="error"
              sx={{
                textAlign: 'center',
                mb: 3,
                p: 2,
                bgcolor: 'rgba(255, 0, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 0, 0, 0.2)'
              }}
            >
              {userRegisterError}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={userForm.handleSubmit(handleUserRegister)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mt: 4
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                autoFocus
                {...userForm.register('first_name', { required: 'First name is required' })}
                error={!!userForm.formState.errors.first_name}
                helperText={userForm.formState.errors.first_name?.message}
                InputProps={{
                  startAdornment: <PersonOutlineIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                {...userForm.register('last_name', { required: 'Last name is required' })}
                error={!!userForm.formState.errors.last_name}
                helperText={userForm.formState.errors.last_name?.message}
                InputProps={{
                  startAdornment: <PersonOutlineIcon sx={{ color: '#5c0f2f', mr: 1 }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#5c0f2f'
                  }
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              {...userForm.register('email', { required: 'Email is required' })}
              error={!!userForm.formState.errors.email}
              helperText={userForm.formState.errors.email?.message}
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <TextField
              fullWidth
              label="Mobile Number"
              {...userForm.register('mobile', { required: 'Mobile number is required' })}
              error={!!userForm.formState.errors.mobile}
              helperText={userForm.formState.errors.mobile?.message}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ color: '#5c0f2f', mr: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#5c0f2f' } }}>Gender</InputLabel>
              <Select
                {...userForm.register('gender', { required: 'Gender is required' })}
                value={userForm.watch('gender') || ''}
                error={!!userForm.formState.errors.gender}
                label="Gender"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#5c0f2f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5c0f2f',
                      borderWidth: 2
                    }
                  }
                }}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
              {userForm.formState.errors.gender && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                  {userForm.formState.errors.gender.message}
                </Typography>
              )}
            </FormControl>
            
            {/* Country Field */}
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name || option}
              value={selectedCountry}
              onChange={(event, newValue) => {
                handleCountryChange(newValue?.id || '');
              }}
              loading={loadingCountries}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country *"
                  error={!!userForm.formState.errors.country}
                  helperText={userForm.formState.errors.country?.message}
                  {...userForm.register('country', { required: 'Country is required' })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <LocationIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                    endAdornment: (
                      <>
                        {loadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Typography variant="body2">
                      {option.name || option}
                    </Typography>
                  </Box>
                );
              }}
            />

            {/* State Field */}
            <Autocomplete
              options={states}
              getOptionLabel={(option) => option.name || option}
              value={selectedState}
              onChange={(event, newValue) => {
                handleStateChange(newValue?.id || '');
              }}
              loading={loadingStates}
              disabled={!selectedCountry}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State *"
                  error={!!userForm.formState.errors.state}
                  helperText={userForm.formState.errors.state?.message}
                  {...userForm.register('state', { required: 'State is required' })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <LocationIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                    endAdornment: (
                      <>
                        {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Typography variant="body2">
                      {option.name || option}
                    </Typography>
                  </Box>
                );
              }}
            />

            {/* City Field */}
            <Autocomplete
              options={cities}
              getOptionLabel={(option) => option.name || option}
              value={selectedCity}
              onChange={(event, newValue) => {
                console.log('City selected:', { cityId: newValue?.id, city: newValue });
                setSelectedCity(newValue);
                userForm.setValue('city', newValue?.id || '', { shouldValidate: true });
              }}
              loading={loadingCities}
              disabled={!selectedState}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City *"
                  error={!!userForm.formState.errors.city}
                  helperText={userForm.formState.errors.city?.message}
                  {...userForm.register('city', { required: 'City is required' })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <LocationIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                    endAdornment: (
                      <>
                        {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:hover fieldset': {
                        borderColor: '#5c0f2f',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5c0f2f',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#5c0f2f'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Typography variant="body2">
                      {option.name || option}
                    </Typography>
                  </Box>
                );
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              {...userForm.register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              error={!!userForm.formState.errors.password}
              helperText={userForm.formState.errors.password?.message}
              InputProps={{
                startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#5c0f2f' }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              {...userForm.register('password_confirmation', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === userForm.watch('password') || 'Passwords do not match',
              })}
              error={!!userForm.formState.errors.password_confirmation}
              helperText={userForm.formState.errors.password_confirmation?.message}
              InputProps={{
                startAdornment: <LockIcon sx={{ color: '#5c0f2f', mr: 1 }} />,
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    sx={{ color: '#5c0f2f' }}
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isRegistering}
              sx={{
                mt: 2,
                py: 2,
                background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4a0d26, #5c0f2f)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(92,15,47,0.3)'
                },
                '&:disabled': {
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  opacity: 0.7
                },
                fontSize: '1.1rem',
                textTransform: 'none',
                borderRadius: '12px',
                transition: 'all 0.3s',
                fontWeight: 600
              }}
            >
              {isRegistering ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  Creating Account...
                </Box>
              ) : (
                'Create Account'
              )}
            </Button>
            <Box sx={{ 
              textAlign: 'center',
              mt: 3,
              p: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(92,15,47,0.08) 0%, rgba(125,31,79,0.08) 100%)',
              border: '1px solid rgba(92,15,47,0.2)',
            }}>
              <Typography variant="body2" sx={{ color: '#5c0f2f', fontWeight: 500 }}>
                Already have an account? 
                <Button 
                  variant="text" 
                  sx={{ 
                    color: '#5c0f2f', 
                    textTransform: 'none',
                    fontWeight: 600,
                    ml: 1,
                    '&:hover': {
                      background: 'rgba(92,15,47,0.1)'
                    }
                  }}
                  onClick={() => {
                    handleCloseUserRegister();
                    navigate('/login-page');
                  }}
                >
                  Sign In
                </Button>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Guest Register Dialog */}
      <Dialog
        open={guestRegisterOpen}
        onClose={handleCloseGuestRegister}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #5c0f2f 0%, #7d1f4f 100%)',
          color: 'white',
          borderRadius: '16px 16px 0 0',
          p: 3,
          fontSize: '1.5rem',
          fontWeight: 700
        }}>
          Guest Registration
          <IconButton onClick={handleCloseGuestRegister} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {guestRegisterError && (
            <Typography
              color="error"
              sx={{
                textAlign: 'center',
                mb: 2,
                p: 1,
                bgcolor: 'rgba(255, 0, 0, 0.1)',
                borderRadius: '4px'
              }}
            >
              {guestRegisterError}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={guestForm.handleSubmit(handleGuestRegister)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 4
            }}
          >
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              {...guestForm.register('email', { required: 'Email is required' })}
              error={!!guestForm.formState.errors.email}
              helperText={guestForm.formState.errors.email?.message}
              InputProps={{
                startAdornment: <EmailIcon sx={{ color: '#5c0f2f', mr: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <TextField
              fullWidth
              label="Mobile Number"
              {...guestForm.register('mobile', { required: 'Mobile number is required' })}
              error={!!guestForm.formState.errors.mobile}
              helperText={guestForm.formState.errors.mobile?.message}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ color: '#5c0f2f', mr: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#5c0f2f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5c0f2f',
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5c0f2f'
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isGustRegistering}
              sx={{
                mt: 2,
                py: 1.5,
                background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #4d0d28, #601233)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(92,15,47,0.3)'
                },
                '&:disabled': {
                  background: 'linear-gradient(45deg, #5c0f2f, #7d1f4f)',
                  opacity: 0.7
                },
                fontSize: '1.1rem',
                textTransform: 'none',
                borderRadius: '8px',
                transition: 'all 0.3s'
              }}
            >
              {isGustRegistering ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  Submitting...
                </Box>
              ) : (
                'Register as Guest'
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RegisterPage;